import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { enqueue } from "../lib/offline";
import { Plus, Trash2, X, Loader2, ClipboardList, Mail, Copy, Send, Repeat } from "lucide-react";
import { toast } from "sonner";

const emptyItem = () => ({ product: "", quantity: 1, unit: "pz" });
const empty = () => ({ supplier_id: null, supplier_name: "", items: [emptyItem()], notes: "", status: "bozza" });

export default function Ordini() {
  const { canWrite } = useAuth();
  const [list, setList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/orders").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, [load]);

  const setItem = (i, patch) => setForm({ ...form, items: form.items.map((x, idx) => idx === i ? { ...x, ...patch } : x) });

  const save = async () => {
    if (!form.supplier_name.trim()) return toast.error("Indica il fornitore");
    setSaving(true);
    const payload = { ...form, items: form.items.map((i) => ({ ...i, quantity: Number(i.quantity) })) };
    try {
      if (!navigator.onLine) {
        await enqueue("order", { ...payload, _autosend: true });
        toast.success("Salvato offline: verrà inviato al fornitore al ritorno della connessione");
        setForm(null); return;
      }
      await api.post("/orders", payload);
      toast.success("Ordine salvato"); setForm(null); load();
    } catch (e) {
      await enqueue("order", { ...payload, _autosend: true });
      toast.warning("Rete assente: salvato offline, invio automatico al ritorno");
      setForm(null);
    } finally { setSaving(false); }
  };

  const remove = async (id) => { if (!window.confirm("Eliminare l'ordine?")) return; await api.delete(`/orders/${id}`); load(); };

  const send = async (o) => {
    if (!navigator.onLine) {
      await enqueue("order", { ...o, _autosend: true });
      toast.success("Offline: l'ordine verrà inviato al fornitore al ritorno della connessione");
      return;
    }
    try { const { data } = await api.post(`/orders/${o.id}/send`); toast.success(`Ordine inviato a ${data.sent_to}`); load(); }
    catch (e) {
      if (!navigator.onLine || e.code === "ERR_NETWORK") {
        await enqueue("order", { ...o, _autosend: true });
        toast.warning("Rete assente: invio automatico al ritorno della connessione");
      } else {
        toast.error(e.response?.data?.detail || "Invio non riuscito");
      }
    }
  };

  const duplicate = (o) => {
    setForm({
      supplier_id: o.supplier_id || null,
      supplier_name: o.supplier_name || "",
      items: (o.items?.length ? o.items : [emptyItem()]).map((i) => ({ product: i.product, quantity: i.quantity, unit: i.unit || "pz" })),
      notes: o.notes || "",
      status: "bozza",
    });
    toast.success("Ordine duplicato: rivedi e salva");
  };

  const copyText = (o) => {
    const txt = `Ordine — ${o.supplier_name}\n\n` + o.items.map((i) => `- ${i.product}: ${i.quantity} ${i.unit}`).join("\n") + (o.notes ? `\n\nNote: ${o.notes}` : "");
    navigator.clipboard?.writeText(txt); toast.success("Testo ordine copiato");
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-navy">Ordini fornitori</h1>
          <p className="text-slate-500">Liste della spesa pronte da inviare al fornitore</p>
        </div>
        {canWrite && <button data-testid="add-order-btn" onClick={() => setForm(empty())} className="flex h-14 items-center gap-2 rounded-xl bg-navy px-5 font-semibold text-white hover:bg-navy-hover"><Plus className="h-5 w-5" /> Nuovo ordine</button>}
      </div>

      {list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <ClipboardList className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-slate-400">Nessun ordine. Crea una lista della spesa per un fornitore.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="flex flex-col rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-head text-lg font-semibold text-navy">{o.supplier_name}</div>
                  <div className="text-sm text-slate-500">{o.items?.length || 0} prodotti</div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${o.status === "inviato" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{o.status === "inviato" ? "Inviato" : "Bozza"}</span>
              </div>
              <ul className="mt-3 space-y-1 text-sm text-slate-600">
                {o.items?.slice(0, 5).map((i, idx) => <li key={idx} className="flex justify-between"><span>{i.product}</span><span className="tnum text-slate-400">{i.quantity} {i.unit}</span></li>)}
              </ul>
              {canWrite && (
                <div className="mt-4 flex gap-2 border-t border-slate-100 pt-3">
                  <button data-testid={`send-order-${o.id}`} onClick={() => send(o)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-gold py-2 text-sm font-bold text-black hover:bg-gold-hover"><Send className="h-4 w-4" /> Invia</button>
                  <button data-testid={`duplicate-order-${o.id}`} onClick={() => duplicate(o)} className="flex items-center justify-center rounded-lg bg-slate-100 px-3 text-navy" title="Riordino rapido: duplica"><Repeat className="h-4 w-4" /></button>
                  <button onClick={() => copyText(o)} className="flex items-center justify-center rounded-lg bg-slate-100 px-3 text-navy" title="Copia testo"><Copy className="h-4 w-4" /></button>
                  <button data-testid={`delete-order-${o.id}`} onClick={() => remove(o.id)} className="flex items-center justify-center rounded-lg bg-red-50 px-3 text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
          <div className="animate-slide-up flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl bg-white sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 p-5">
              <h3 className="font-head text-xl font-bold text-navy">Nuovo ordine</h3>
              <button onClick={() => setForm(null)}><X className="h-6 w-6 text-slate-400" /></button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              <div>
                <label className="mb-1 block text-sm font-semibold text-navy">Fornitore *</label>
                <input data-testid="order-supplier" list="ord-sup" value={form.supplier_name} onChange={(e) => { const s = suppliers.find((x) => x.name === e.target.value); setForm({ ...form, supplier_name: e.target.value, supplier_id: s?.id || null }); }} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
                <datalist id="ord-sup">{suppliers.map((s) => <option key={s.id} value={s.name} />)}</datalist>
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-semibold text-navy">Prodotti</label>
                  <button data-testid="add-order-item" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })} className="flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium hover:bg-slate-200"><Plus className="h-4 w-4" /> Riga</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2">
                      <input placeholder="Prodotto" value={it.product} onChange={(e) => setItem(i, { product: e.target.value })} className="col-span-7 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" data-testid={`order-product-${i}`} />
                      <input type="number" placeholder="Qtà" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} className="col-span-3 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" />
                      <input placeholder="Un." value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })} className="col-span-2 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold" />
                    </div>
                  ))}
                </div>
              </div>
              <textarea placeholder="Note per il fornitore" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full rounded-lg border-2 border-slate-200 p-3 text-sm outline-none focus:border-gold" />
            </div>
            <div className="border-t border-slate-100 p-5">
              <button data-testid="save-order-btn" onClick={save} disabled={saving} className="flex h-14 w-full items-center justify-center gap-2 rounded-lg bg-gold font-bold text-black hover:bg-gold-hover disabled:opacity-60">{saving && <Loader2 className="h-5 w-5 animate-spin" />} Salva ordine</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
